import { Component, Inject, OnInit } from '@angular/core'
import {
    Action,
    selfValuesFactory,
    arrayComparatorFactory,
    libArrayValuesToMap,
    Via,
    compareDates,
    TableControlType,
    Table,
    FluentTracker,
    FieldTracker,
    isPositiveInt,
    HandbookService,
    ConfirmationService,
    Entry,
    isZeroOrPositiveInt,
    TableDelegate,
    SmartColumns,
    stringToMap,
    TableExportType,
    libValuesToMap,
    booleanComparator,
    LabelProvider, im, ErrorsT
} from '@surgetech-eu/core'
import { of, Observable, Subscriber } from 'rxjs'

import moment from 'moment'
import uuid from 'uuid'
import { map, tap } from 'rxjs/operators'
import { ComponentType, ComponentTypeService } from '../services/component-type.service'
import { UnitOfMeasureService } from '../services/unit-of-measure.service'
import { disabledColumnOptions, maxRank } from '../constant'
import { UnitOfMeasure } from '../models'
import { ActivatedRoute } from '@angular/router'
import { SortRank } from '../helpers'

export enum columnName {
    key = 'key',
    description = 'description',
    uom = 'units',
    disabled = 'disabled',
    rank = 'rank'
}
@Component({
    selector: 'component-type',
    templateUrl: './component-type.component.html',
    styleUrls: ['./component-type.component.scss'],
    standalone: false
})
@SmartColumns()
export class ComponentTypeComponent implements TableDelegate, OnInit {
    private table: Table

    saving = false
    saved = false

    error: ErrorsT = null

    private fluentTracker: FluentTracker
    private pendingSource: ComponentType[] = []
    private unitOfMeasure: UnitOfMeasure[] = []
    public uomOptions: Map<string, string> = new Map<string, string>()
    private labels: LabelProvider

    importUrl = '/libraries/management/types/componentType/import-entries'

    constructor(
        @Inject('surge.core.schema-transformers') private schemaTransformers: any[],
        private componentTypeService: ComponentTypeService,
        private handbookService: HandbookService,
        private confirmationService: ConfirmationService,
        private unitOfMeasureService: UnitOfMeasureService,
        private route: ActivatedRoute
    ) {
        this.labels = route.snapshot.data.labels
        this.uomOptions = route.snapshot.data.unitOfMeasure.unitOfMeasureOptions
        this.fluentTracker = new FluentTracker(new FieldTracker(), {
            fields: {
                id: {
                    ns: true
                },
                key: {
                    required: true,
                    validate: (value, extra, withinNs) => {
                        if (!value) {
                            return "It's required"
                        }
                        if (
                            this.pendingSource.findIndex(
                                item =>
                                    item.key?.trim()?.toLowerCase() === value?.trim()?.toLowerCase() &&
                                    item.id.trim().toLowerCase() !== withinNs.trim()?.toLowerCase()
                            ) !== -1
                        ) {
                            return 'Should have a unique value'
                        }
                        return null
                    }
                },
                description: {
                    required: false
                },
                units: {
                    required: true,
                    validate: (v, extra, withinNs) => {
                        const errors = []
                        if (!v?.length) {
                            return "It's required"
                        }
                        if (v && typeof v[0] === 'object') {
                            if (v?.length) {
                                v.map(item => {
                                    if (!this.uomOptions.get(item.id?.toLowerCase())) {
                                        errors.push(item?.code ? item?.id + '-' + item?.code : item?.id)
                                    }
                                })
                                if (errors.length) {
                                    errors.push('not found')
                                    return errors
                                }
                            }
                        }
                        return null
                    }
                },
                rank: {
                    required: false,
                    validate: value => {
                        if (value === null) {
                            return null
                        }
                        if (!isPositiveInt(value)) {
                            return 'Should be a positive number'
                        }
                        if (value > maxRank) {
                            return im('It must be less than {maxRank}', { maxRank })
                        }
                        return null
                    }
                },
                disabled: {
                    required: false
                },
                new: {
                    required: false
                },
                library: {
                    required: false
                },
                namespace: {
                    required: true
                },
                fieldsToReset: {
                    required: false
                }
            }
        })
        this.fluentTracker.listen(
            FluentTracker.EVENT_CHANGE,
            () => {
                this.error = null
                this.table.refreshSizes()
            },
            true
        )
    }
    ngOnInit() {
        this.unitOfMeasureService.getAll().subscribe(res => {
            this.unitOfMeasure = res
        })
    }

    onTableDelegateBind(table: Table): void {
        this.table = table
    }

    disableSupportQuery(): boolean {
        return true
    }

    getColumnsConfig() {
        return [
            {
                name: columnName.key,
                path: 'key',
                title: this.labels.get('libraries.componentTypeTable.key'),
                type: 'text'
            },
            {
                name: columnName.description,
                path: 'description',
                title: this.labels.get('libraries.componentTypeTable.description'),
                type: 'text'
            },
            {
                name: columnName.uom,
                path: 'units.id',
                title: this.labels.get('libraries.componentTypeTable.units'),
                type: 'text'
            },
            {
                name: 'createdAt',
                title: this.labels.get('libraries.componentTypeTable.createdAt'),
                type: 'date'
            },
            {
                name: 'createdBy',
                title: this.labels.get('libraries.componentTypeTable.createdBy'),
                type: 'text'
            },
            {
                name: 'updatedAt',
                title: this.labels.get('libraries.componentTypeTable.updatedAt'),
                type: 'date'
            },
            {
                name: 'updatedBy',
                title: this.labels.get('libraries.componentTypeTable.updatedBy'),
                type: 'text'
            },
            {
                name: columnName.rank,
                path: 'rank',
                title: this.labels.get('libraries.componentTypeTable.rank')
            },
            {
                name: columnName.disabled,
                title: this.labels.get('libraries.componentTypeTable.disabled')
            }
        ]
    }

    customizeColumns(columnsMap) {
        const keyColumn = columnsMap.get(columnName.key)
        keyColumn.control = {
            type: TableControlType.INPUT,
            changing: (value, row) => this.updateFluentValue(value, row, columnName.key),
            canShow: (value, row) => row.new,
            failed: (v, row) => this.fluentTracker.tracker.hasError(row.id, columnName.key),
            failure: (v, row) => this.fluentTracker.tracker.getError(row.id, columnName.key),
            seed: row => {
                return this.fluentTracker.tracker.getValue(row.id, columnName.key) || row.key
            }
        }
        keyColumn.filter = {
            via: Via.TEXT
        }

        const descriptionColumn = columnsMap.get(columnName.description)
        descriptionColumn.control = {
            type: TableControlType.INPUT,
            changing: (value, row) => this.updateFluentValue(value, row, columnName.description),
            canShow: () => true,
            failed: (v, row) => this.fluentTracker.tracker.hasError(row.id, columnName.description),
            failure: (v, row) => this.fluentTracker.tracker.getError(row.id, columnName.description),
            seed: row => {
                return this.fluentTracker.tracker.getValue(row.id, columnName.description) || row.description
            }
        }
        descriptionColumn.filter = {
            via: Via.TEXT
        }

        const UoMColumn = columnsMap.get(columnName.uom)
        UoMColumn.control = {
            type: TableControlType.SELECT,
            multiple: true,
            values: (v, row) => {
                const options = new Map<string, string>()
                this.unitOfMeasure.sort(SortRank).map(item => {
                    options.set(
                        item.key?.toLowerCase(),
                        item.description ? item.key + ' - ' + item.description : item.key
                    )
                })
                v?.forEach(item => {
                    if (item && !this.uomOptions.get(item?.id?.toLowerCase())) {
                        options.set(item.id?.toLowerCase(), item.code ? item.id + ' - ' + item.code : item.id)
                    }
                })
                return options
            },
            change: (value, row) => {
                value = value?.map(valueItem => {
                    const find = this.unitOfMeasure.find(item => item.key?.toLowerCase() === valueItem?.toLowerCase())
                    if (find) {
                        return {
                            id: find.key,
                            code: find.description
                        }
                    } else {
                        return {
                            id: row.units?.find(item => item.id?.toLowerCase() === valueItem?.toLowerCase())?.id,
                            code: row.units?.find(item => item.id?.toLowerCase() === valueItem?.toLowerCase())?.code
                        }
                    }
                })
                if (value?.length) {
                    this.updateFluentValue(value, row, columnName.uom)
                } else {
                    this.updateFluentValue(null, row, columnName.uom)
                }
            },
            canShow: () => true,
            failed: (v, row) => this.fluentTracker.tracker.hasError(row.id, columnName.uom),
            failure: (v, row) => this.fluentTracker.tracker.getError(row.id, columnName.uom),
            seed: row => {
                return (
                    this.fluentTracker.tracker.getValue(row.id, columnName.uom)?.map(item => item.id?.toLowerCase()) ||
                    row.units?.map(item => item.id?.toLowerCase())
                )
            }
        }
        UoMColumn.filter = {
            via: Via.SELFPICKER,
            selfValues: selfValuesFactory('units', libArrayValuesToMap),
            comparator: arrayComparatorFactory(v => (v ? v.map(item => item.id) : [])),
            values: this.uomOptions
        }

        const createdAtColumn = columnsMap.get('createdAt')
//         createdAtColumn.pipe = v => (v ? moment(v).format(DD/MM/YYYY) : )
        createdAtColumn.filter = {
            via: Via.MOMENT
        }
        createdAtColumn.sorting = {
            sortable: true,
            compare: compareDates('createdAt')
        }

        const createdByColumn = columnsMap.get('createdBy')
        createdByColumn.filter = {
            via: Via.SELF,
            selfValues: selfValuesFactory('createdBy', stringToMap),
            path: 'createdBy'
        }

        const updatedAtColumn = columnsMap.get('updatedAt')
//         updatedAtColumn.pipe = v => (v ? moment(v).format(DD/MM/YYYY) : )
        updatedAtColumn.filter = {
            via: Via.MOMENT
        }
        updatedAtColumn.sorting = {
            sortable: true,
            compare: compareDates('updatedAt')
        }

        const updatedByColumn = columnsMap.get('updatedBy')
        updatedByColumn.filter = {
            via: Via.SELF,
            selfValues: selfValuesFactory('updatedBy', stringToMap),
            path: 'updatedBy'
        }

        const rankColumn = columnsMap.get(columnName.rank)
        rankColumn.control = {
            type: TableControlType.INPUT,
            changing: (value, row) => this.updateFluentValue(value, row, columnName.rank),
            canShow: () => true,
            failed: (v, row) => this.fluentTracker.tracker.hasError(row.id, columnName.rank),
            failure: (v, row) => this.fluentTracker.tracker.getError(row.id, columnName.rank),
            seed: row => {
                return this.fluentTracker.tracker.getValue(row.id, columnName.rank) || row.rank
            }
        }
        rankColumn.sorting = {
            sortable: true
        }
        rankColumn.filter = {
            via: Via.TEXT
        }

        const disableColumn = columnsMap.get(columnName.disabled)
        disableColumn.control = {
            type: TableControlType.DROPDOWN,
            values: disabledColumnOptions,
            required: true,
            change: (value, row) => {
                this.updateFluentValue(value, row, columnName.disabled)
            },
            disabled: () => false,
            canShow: () => true,
            seed: row => this.fluentTracker.tracker.getValue(row.id, columnName.disabled) || row.disabled.toString()
        }
        disableColumn.sorting = {
            sortable: false
        }
        disableColumn.filter = {
            via: Via.DROPDOWN,
            values: disabledColumnOptions,
            comparator: booleanComparator
        }
    }

    canTableRemove(): boolean {
        return true
    }

    canTableExport(): boolean {
        return true
    }

    getTableExportTypes(): TableExportType[] {
        return [
            {
                name: 'xlsx',
                url: '/libraries/management/types/ComponentType/export-entries/xlsx'
            }
        ]
    }

    mapBeforeTableExportDirectly(rows: any[]): any[] {
        this.fluentTracker.validateAll()
        if (!this.fluentTracker.hasErrors()) {
            return rows.map(item => {
                delete item.__index
                delete item.__internalRowNumbers
                delete item.__sortIndex
                delete item.new
                return item
            })
        } else {
            return rows
                .map(item => {
                    delete item.__index
                    delete item.__internalRowNumbers
                    delete item.__sortIndex
                    return item
                })
                .filter(item => !item.new)
        }
    }

    generateTableExportFilename(type: TableExportType): string {
        return 'BC-' + moment().format('YYYY-MM-DD') + '-Handbook-componentType.xlsx'
    }

    get importTemplateUrl() {
        return `/libraries/management/types/ComponentType/import-entries/template.xlsx`
    }

    shouldTableExportDirectly() {
        return true
    }

    importFailed(error) {
        if (typeof error === 'string') {
            this.error = error
        } else {
            this.error = 'Unknown error'
        }
    }

    beforeImport() {
        this.error = null
        this.saved = false
    }

    afterImport(imported) {
        if (imported.length > 0) {
            const entriesByKey: { [key: string]: Entry } = {}
            this.table.getAllData().forEach(row => (entriesByKey[row.key] = row))

            let hasDuplicates = false
            let hasRankOverflow = false
            let hasNotIntRank = false
            imported.forEach(entry => {
                const found = entriesByKey[entry.key]
                if (found) {
                    hasDuplicates = true
                }
                if (entry.rank && !isZeroOrPositiveInt(entry.rank)) {
                    hasNotIntRank = true
                }
                if (entry.rank > maxRank) {
                    hasRankOverflow = true
                }

                const newItem = Object.assign({}, this.createNewComponentType(), entry)
                Object.keys(newItem).forEach(value => {
                    if (value === 'disabled') {
                        this.fluentTracker.update(newItem.id, value, newItem[value].toString())
                    } else if (value === 'units') {
                        if (newItem[value]?.length) {
                            newItem[value] = newItem[value].map(item => {
                                const findUOMItem = this.unitOfMeasure.find(
                                    UOMItem => UOMItem.key?.toLowerCase() === item.id?.toLowerCase()
                                )
                                if (findUOMItem) {
                                    return {
                                        id: findUOMItem?.key,
                                        code: findUOMItem?.description
                                    }
                                }
                                return item
                            })
                            this.updateFluentValue(newItem[value], newItem, columnName.uom)
                        } else {
                            this.updateFluentValue([], newItem, columnName.uom)
                            this.fluentTracker.tracker.addError(newItem.id, columnName.uom, "It's required")
                        }

                        const errors: ErrorsT = []
                        newItem.units.forEach(item => {
                            const findUOMItem = this.unitOfMeasure.find(
                                UOMItem => UOMItem.key?.toLowerCase() === item.id?.toLowerCase()
                            )
                            if (!this.uomOptions.get(item.id?.toLowerCase()) && item.id) {
                                if (findUOMItem) {
                                    errors.push(
                                        findUOMItem.description
                                            ? findUOMItem.key + '-' + findUOMItem.description
                                            : findUOMItem.key
                                    )
                                } else {
                                    errors.push(item.code ? item.id + '-' + item.code : item.id)
                                }
                            }
                        })

                        if (errors.length) {
                            errors.push('not found')
                            this.fluentTracker.tracker.addError(newItem.id, columnName.uom, errors)
                        }
                    } else {
                        this.fluentTracker.update(newItem.id, value, newItem[value])
                    }
                })
                this.pendingSource.push(newItem)
                this.updateExistFields(newItem)
            })
            let error = []
            if (hasDuplicates) {
                error.push('The key must be unique.')
            }
            if (hasNotIntRank || hasRankOverflow) {
                error.push(im('Rank should be a positive number and less than {maxRank}.', { maxRank }))
            }
            if (error?.length) {
                error.push('Please check the data.')
            }
            if (this.fluentTracker.hasErrors()) {
                if (!error?.length) {
                    error.push('Some of the imported entries have errors. Please check the data.')
                }
                this.error = error
            }
            this.table.goToLastPage()
            this.table.reload()
        }
    }

    configureTableRemoveAction(action: Action) {
        action.disabled = (row: ComponentType) => row.isUsed
        action.run = (row: ComponentType) => {
            return new Observable(observer => {
                const confirmation = this.confirmationService.openWithParams({
                    text: 'Are you sure that you want to delete this entry?',
                    title: 'Delete'
                })
                confirmation
                    .then(() => {
                        this.error = null
                        this.saved = false

                        if (row.new) {
                            this.removeItem(row.id)
                            return of(true)
                        }
                        delete row.__index
                        delete row.__internalRowNumbers
                        delete row.__sortIndex
                        return this.componentTypeService.remove([row]).subscribe(
                            data => {
                                this.removeItem(row.id)
                                observer.next(data)
                            },
                            error => {
                                this.error = error
                            }
                        )
                    })
                    .catch(() => {
                        observer.next(true)
                    })
            })
        }
    }

    hasTableErrorForRow(row) {
        return this.fluentTracker.hasErrors(row.id)
    }

    getTableData(): Observable<ComponentType[]> {
        if (this.pendingSource.length) {
            return of(this.pendingSource)
        }

        return this.componentTypeService.getAll().pipe(
            map(data =>
                data.map(componentType => {
                    return {
                        ...componentType
                    }
                })
            ),
            tap(data => (this.pendingSource = [...data]))
        )
    }

    canSave(): boolean {
        return this.fluentTracker.hasValues() && !this.fluentTracker.hasErrors() && !this.saving
    }

    private convertPayload(): { create?: ComponentType[]; update?: ComponentType[] } | [] {
        const fluentTracker = this.fluentTracker.generate()
        if (!this.fluentTracker.hasErrors()) {
            const data = {
                create: [],
                update: []
            }
            fluentTracker.forEach(item => {
                item.disabled = item.disabled === 'true'
                const findElem = this.unitOfMeasure.find(unit => item?.unitOfMeasure?.id === unit.key)
                if (findElem) {
                    item.unitOfMeasure = { id: findElem.key, code: findElem.description }
                }
                if (item.new) {
                    delete item.new
                    data.create.push({ ...item })
                } else {
                    data.update.push({ ...item })
                }
            })
            return data
        } else {
            this.table.refresh()
            return []
        }
    }

    save(): void {
        const data = this.convertPayload()
        this.error = null
        this.saved = false
        if (!Array.isArray(data)) {
            const { create, update } = data
            if (create.length) {
                this.saving = true
                this.componentTypeService.create(create).subscribe(
                    response => {
                        this.pendingSource = []
                        this.fluentTracker.clearAll()
                        this.table.reload(
                            new Subscriber(() => {
                                this.saving = false
                                this.saved = true
                            })
                        )
                    },
                    error => {
                        let errors = ''
                        if (typeof error === 'object') {
                            Object.entries(error).forEach(([key, value]) => {
                                errors += key + ' ' + value + '; '
                            })
                            this.error = errors
                            this.saving = false
                            return
                        }
                        this.saving = false
                        this.error = error
                    }
                )
            }
            if (update.length) {
                this.saving = true
                this.componentTypeService.update(update).subscribe(
                    response => {
                        this.pendingSource = []
                        this.fluentTracker.clearAll()
                        this.table.reload(
                            new Subscriber(() => {
                                this.saving = false
                                this.saved = true
                            })
                        )
                    },
                    error => {
                        let errors = ''
                        if (typeof error === 'object') {
                            Object.entries(error).forEach(([key, value]) => {
                                errors += key + ' ' + value + '; '
                            })
                            this.error = errors
                            this.saving = false
                            return
                        }
                        this.saving = false
                        this.error = error
                    }
                )
            }
        }
    }

    addNewItem(): void {
        const newItem = this.createNewComponentType()
        this.pendingSource.splice(this.pendingSource.length + 1, 0, newItem)
        this.updateExistFields(newItem)
        this.table.reload()
        this.table.goToLastPage()
    }

    private createNewComponentType(): ComponentType {
        const id = uuid()
        return {
            key: '',
            description: '',
            units: null,
            disabled: false,
            rank: null,
            id,
            new: true,
            library: 'componentType',
            namespace: 'kering'
        }
    }

    private removeItem(id: string) {
        this.fluentTracker.tracker.destroy(id)
        const removedIndex = this.pendingSource.findIndex(md => md.id === id)
        this.pendingSource.splice(removedIndex, 1)

        this.table.reload()
    }

    private updateExistFields(row: ComponentType): void {
        if (!this.fluentTracker.tracker.hasValue(row.id, columnName.key) && row.key) {
            this.fluentTracker.update(row.id, columnName.key, row.key)
        }
        if (!this.fluentTracker.tracker.hasValue(row.id, 'library')) {
            this.fluentTracker.update(row.id, 'library', row.library)
        }
        if (!this.fluentTracker.tracker.hasValue(row.id, 'namespace')) {
            this.fluentTracker.update(row.id, 'namespace', row.namespace)
        }
        if (!this.fluentTracker.tracker.hasValue(row.id, columnName.description) && row.description) {
            this.fluentTracker.update(row.id, columnName.description, row.description)
        }
        if (!this.fluentTracker.tracker.hasValue(row.id, columnName.uom) && row.units?.length) {
            this.fluentTracker.update(row.id, columnName.uom, row.units)
        }
        if (!this.fluentTracker.tracker.hasValue(row.id, columnName.disabled) && row.disabled.toString()) {
            this.fluentTracker.update(row.id, columnName.disabled, row.disabled.toString())
        }
        if (!this.fluentTracker.tracker.hasValue(row.id, columnName.rank) && row.rank) {
            this.fluentTracker.update(row.id, columnName.rank, row.rank)
        }
        if (!this.fluentTracker.tracker.hasValue(row.id, 'new')) {
            this.fluentTracker.update(row.id, 'new', row.new)
        }
    }

    private updateFluentValue(value, row, column: string) {
        const indexToUpdate = this.pendingSource.findIndex(mc => mc.id === row.id)
        if (indexToUpdate !== -1) {
            this.pendingSource[indexToUpdate][column] = value
            row[column] = value
        }
        this.updateExistFields(row)
        this.fluentTracker.update(row.id, column, value)
    }
}
